import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { biApi } from '../api/bi'
import { colors, font, text, radius, sx } from '../design/tokens'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import CampoFecha from '../components/CampoFecha'
import {
  SEP, COMO, RESUMEN, armarTabla, compilar, crearCatalogo, etiqueta, etiquetaColumna,
  fmtCelda, fmtEntero, nombreValor, numExcel, valoresDistintos,
  ATAJOS_PERIODO, BASES_FECHA, UNIDADES_FECHA, esFechaVariable, etiquetaFechaVariable, resolverFecha,
} from '../utils/cuboVentas'
import { fmtFecha } from '../utils/fecha'

// BI → Cubo (Martin, 4 oct 2026): el cubo de ventas al estilo del de Presea.
// 1) se elige el PERÍODO; 2) todos los campos del renglón de venta van a
// Filas / Columnas / Filtros, los números a Valores; 3) sobre la vista se hacen
// cálculos (fórmulas entre valores) y "mostrar como"; 4) las vistas se guardan
// por persona. Los renglones llegan crudos de Presea (/api/bi/cubo/lineas) y la
// tabla dinámica se arma acá (utils/cuboVentas.js).
// 6 oct 2026 (Martin): los campos van ARRIBA como tags, cada uno con su menú
// para ubicarlo en Filas o Columnas y su filtro múltiple; totales de fila y de
// columna se ocultan por separado y siempre van sombreados; los cálculos pueden
// comparar columnas ([Facturado · oct 2026] - [Facturado · sep 2026]) y lo que
// usan puede no estar a la vista (columnas filtradas, valores ocultos).

const VISTA_INICIAL = { fila: ['rubro'], col: ['mes'], val: [{ campo: 'facturado', res: 'sum', como: 'val' }], fil: {}, calcs: [], subtot: true, totFila: true, totCol: true }
const ZONAS = { fila: 'Filas', col: 'Columnas' }
// Sombra de los totales (Martin, 6 oct 2026: "los campos de totales siempre sombreados").
const SOMBRA = '#e8edf4', SOMBRA_FUERTE = '#dbe3ee'

// Reglas que necesitan CSS real (sticky, hover, selectores de tabla); el resto va inline.
const CSS = `
.cv-tabla { border-collapse: separate; border-spacing: 0; width: max-content; min-width: 100%; }
.cv-tabla th, .cv-tabla td { border-right: 1px solid ${colors.border}; border-bottom: 1px solid ${colors.border}; padding: 4px 8px; white-space: nowrap; }
.cv-tabla thead th { position: sticky; background: #eef2f7; font-weight: 600; font-size: 12px; z-index: 2; text-align: right; color: ${colors.text}; }
.cv-tabla thead th.rh { text-align: left; left: 0; z-index: 4; }
.cv-tabla thead th.ch { text-align: center; }
.cv-tabla thead th.vh { color: ${colors.textSec}; font-weight: 500; }
.cv-tabla thead th.vh.c { color: #6d28d9; }
.cv-tabla td { text-align: right; font-family: ${font.mono}; font-variant-numeric: tabular-nums; font-size: 12px; color: ${colors.text}; }
.cv-tabla td.rl { text-align: left; font-family: ${font.sans}; font-size: 12.5px; position: sticky; left: 0; background: ${colors.bg}; z-index: 1; max-width: 320px; overflow: hidden; text-overflow: ellipsis; }
.cv-tabla td.rl.vacia { color: transparent; }
.cv-tabla tr.sub td { background: ${SOMBRA}; font-weight: 600; }
.cv-tabla tr.tot td { background: ${SOMBRA_FUERTE}; font-weight: 700; position: sticky; bottom: 0; z-index: 2; border-top: 1px solid #94a3b8; }
.cv-tabla tr.tot td.rl { z-index: 3; }
.cv-tabla td.tc, .cv-tabla thead th.tc { background-color: ${SOMBRA}; font-weight: 600; }
.cv-tabla tr.sub td.tc, .cv-tabla tr.tot td.tc { background-color: ${SOMBRA_FUERTE}; }
.cv-tabla tbody tr.fl:hover td.tc { background-color: ${SOMBRA_FUERTE}; }
.cv-tag:hover { border-color: ${colors.linkBorder} !important; }
.cv-tabla td.neg { color: ${colors.errStrong}; }
.cv-tabla td.cv { color: #6d28d9; }
.cv-tabla tbody tr.fl:hover td:not(.tc) { background-color: ${colors.linkBg}; }
.cv-campo { display: flex; width: 100%; text-align: left; border: 0; background: none; padding: 4px 8px; cursor: grab; user-select: none; font-size: 13px; color: ${colors.text}; border-radius: 4px; }
.cv-campo:hover { background: ${colors.linkBg}; }
.cv-chip:hover { border-color: ${colors.linkBorder} !important; }
.cv-opt { display: block; width: 100%; text-align: left; border: 0; background: none; padding: 5px 8px; border-radius: 4px; font-size: 13px; color: ${colors.text}; cursor: pointer; }
.cv-opt:hover { background: ${colors.bgSoft}; }
.cv-atajo:hover { border-color: ${colors.linkBorder} !important; color: ${colors.link} !important; }
`

export default function BiCuboVentasPage() {
  const [rangoDatos, setRangoDatos] = useState(null)   // {desde, hasta, max_lineas}
  // Cada punta es una fecha fija ('AAAA-MM-DD') o una variable {base, n, unidad}
  // (15 oct 2026): la vista guardada conserva la variable y se mueve con el calendario.
  // Arranca en "Este mes" de entrada: antes se ponía al llegar el rango de
  // ventas y, si la persona ya estaba escribiendo, le pisaba lo escrito.
  const [periodo, setPeriodo] = useState({ desde: ATAJOS_PERIODO[0][1], hasta: ATAJOS_PERIODO[0][2] })
  const [D, setD] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [E, setE] = useState(VISTA_INICIAL)
  const [vistas, setVistas] = useState([])
  const [vistaSel, setVistaSel] = useState('')
  const [pop, setPop] = useState(null)         // {x, y, contenido}
  const [dlgCalc, setDlgCalc] = useState(false)
  const [dlgGuardar, setDlgGuardar] = useState(false)
  const [toast, setToast] = useState(null)
  const arrastreRef = useRef(null)
  const toastTimerRef = useRef(null)

  const avisar = useCallback((m) => { setToast(m); clearTimeout(toastTimerRef.current); toastTimerRef.current = setTimeout(() => setToast(null), 2600) }, [])

  useEffect(() => {
    biApi.cuboRango().then(({ data }) => setRangoDatos(data)).catch(() => setError('No se pudo leer el período disponible.'))
    biApi.cuboVistas().then(({ data }) => setVistas(data)).catch(() => {})
  }, [])

  // Recibe las puntas tal como están (fijas o variables) y las conserva: lo
  // que viaja al servidor son las fechas resueltas.
  const cargar = useCallback(async (pDesde, pHasta) => {
    let desde = resolverFecha(pDesde), hasta = resolverFecha(pHasta)
    if (!desde || !hasta) { avisar('Elegí las dos fechas'); return }
    if (desde > hasta) { [desde, hasta] = [hasta, desde]; [pDesde, pHasta] = [pHasta, pDesde] }
    setCargando(true); setError(null)
    try {
      const { data } = await biApi.cuboLineas({ desde, hasta })
      setPeriodo({ desde: pDesde, hasta: pHasta }); setD(data)
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'No se pudieron traer las ventas del período.')
    } finally { setCargando(false) }
  }, [avisar])

  const cat = useMemo(() => (D ? crearCatalogo(D) : null), [D])
  const N = D ? D.cols[0].length : 0

  const tabla = useMemo(() => {
    if (!cat || (!E.val.length && !E.calcs.length)) return null
    return armarTabla(D, cat, E)
  }, [D, cat, E])

  // ── Cambios de la vista ──
  const upd = (fn) => { setVistaSel(''); setE(prev => { const n = JSON.parse(JSON.stringify(prev)); fn(n); return n }) }
  function agregarA(n, z, campo, pos, extra) {
    const c = cat.porId[campo]
    if (z === 'val') { n.val.splice(pos ?? n.val.length, 0, extra?.campo ? extra : { campo, res: c.numIdx != null ? 'sum' : 'dist', como: 'val' }); return }
    if (c.numIdx != null) { avisar(c.label + ' es un número: va en Valores'); return }
    n.fila = n.fila.filter(x => x !== campo); n.col = n.col.filter(x => x !== campo)
    if (z === 'fil') { n.fil[campo] = extra?.fil || n.fil[campo] || []; return }
    n[z].splice(pos ?? n[z].length, 0, campo)
  }
  function quitarDe(n, z, i) {
    if (z === 'val') return n.val.splice(+i, 1)[0]
    if (z === 'fil') { const v = n.fil[i]; delete n.fil[i]; return { campo: i, fil: v } }
    return n[z].splice(+i, 1)[0]
  }

  // ── Drag & drop ──
  function soltar(e, dest) {
    e.preventDefault()
    const a = arrastreRef.current; arrastreRef.current = null
    if (!a) return
    const chips = [...e.currentTarget.querySelectorAll('[data-chip]')]
    const horizontal = dest !== 'val'
    let pos = chips.findIndex(c => { const r = c.getBoundingClientRect(); return horizontal ? e.clientX < r.left + r.width / 2 : e.clientY < r.top + r.height / 2 })
    if (pos < 0) pos = chips.length
    upd(n => {
      if (a.campo) { agregarA(n, dest, a.campo, pos); return }
      if (a.z === dest && dest !== 'fil') {
        const arr = dest === 'val' ? n.val : n[dest]
        const [it] = arr.splice(+a.i, 1); if (pos > +a.i) pos--; arr.splice(pos, 0, it); return
      }
      const it = quitarDe(n, a.z, a.i)
      const campo = a.z === 'fila' || a.z === 'col' ? it : it.campo
      agregarA(n, dest, campo, pos, a.z === 'fil' || (a.z === 'val' && dest === 'val') ? it : null)
    })
  }

  // ── Popovers ──
  const abrirPop = (e, contenido) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setPop({ x: Math.max(16, Math.min(r.left, window.innerWidth - 300)), y: r.bottom + 4, contenido }) }
  useEffect(() => {
    const cerrar = () => setPop(null)
    const esc = (e) => { if (e.key === 'Escape') setPop(null) }
    document.addEventListener('click', cerrar); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('click', cerrar); document.removeEventListener('keydown', esc) }
  }, [])

  /** Menú de un tag de campo: dónde va (Filas / Columnas / sin ubicar), orden y filtro múltiple. */
  function menuTag(e, id) {
    const c = cat.porId[id]
    if (c.numIdx != null) {
      abrirPop(e, <>
        <div style={st.popTit}>{c.label}</div>
        <button className="cv-opt" onClick={() => { setPop(null); upd(n => agregarA(n, 'val', id)) }}>Agregar a Valores</button>
      </>)
      return
    }
    const z = E.fila.includes(id) ? 'fila' : E.col.includes(id) ? 'col' : null
    const i = z ? E[z].indexOf(id) : -1
    abrirPop(e, <>
      <div style={st.popTit}>{c.label}</div>
      <div style={st.segmento} role="group" aria-label="Ubicación">
        {[['fila', 'Filas'], ['col', 'Columnas'], [null, 'Sin ubicar']].map(([k, t]) => (
          <button key={t} type="button" style={{ ...st.segBtn, ...(z === k ? st.segOn : null) }}
            onClick={() => { setPop(null); if (z !== k) upd(n => { n.fila = n.fila.filter(x => x !== id); n.col = n.col.filter(x => x !== id); if (k) n[k].push(id) }) }}>{t}</button>
        ))}
      </div>
      {z && E[z].length > 1 && (
        <div style={{ display: 'flex', gap: 4, padding: '4px 6px' }}>
          <Boton sm variante="ghost" disabled={i === 0} onClick={() => { setPop(null); upd(n => { n[z].splice(i, 1); n[z].splice(i - 1, 0, id) }) }}>← Antes</Boton>
          <Boton sm variante="ghost" disabled={i === E[z].length - 1} onClick={() => { setPop(null); upd(n => { n[z].splice(i, 1); n[z].splice(i + 1, 0, id) }) }}>Después →</Boton>
        </div>
      )}
      {z === 'col' && <div style={{ ...st.nota, padding: '2px 8px' }}>En Columnas, el filtro elige qué columnas se ven: las ocultas siguen disponibles para los cálculos.</div>}
      <MenuFiltro key={id} id={id} cat={cat} N={N} elegidos={E.fil[id] || []}
        onAplicar={(sel) => { setPop(null); upd(n => { if (sel === null || !sel.length) delete n.fil[id]; else n.fil[id] = sel }) }} />
    </>)
  }
  function menuChip(e, z, i) {
    if (z === 'val') { menuValor(e, +i); return }
    menuTag(e, E[z][+i])
  }
  function menuValor(e, i) {
    const v = E.val[i], c = cat.porId[v.campo]
    const res = c.numIdx != null ? Object.keys(RESUMEN) : ['dist', 'count']
    abrirPop(e, <MenuValor v={v} res={res} nombre={nombreValor(cat, v)} nombreBase={nombreValor(cat, { ...v, nombre: '' })}
      onCambio={(cambio) => {
        setPop(null)
        upd(n => {
          const viejo = nombreValor(cat, n.val[i])
          if (cambio === 'quitar') { n.val.splice(i, 1); return }
          Object.assign(n.val[i], cambio)
          const nuevo = nombreValor(cat, n.val[i])
          if (viejo !== nuevo) n.calcs.forEach(cc => { cc.formula = cc.formula.split('[' + viejo + ']').join('[' + nuevo + ']') })
        })
      }} />)
  }
  // ── Vistas ──
  async function guardarVista(nombre) {
    try {
      const { data } = await biApi.cuboGuardarVista({ nombre, config: { ...E, periodo } })
      const { data: lista } = await biApi.cuboVistas()
      setVistas(lista); setVistaSel(String(data.id)); setDlgGuardar(false); avisar('Vista «' + nombre + '» guardada')
    } catch (e) { avisar(e.response?.data?.message || 'No se pudo guardar la vista') }
  }
  async function elegirVista(id) {
    setVistaSel(id)
    const v = vistas.find(x => String(x.id) === id); if (!v) return
    const { periodo: p, ...resto } = v.config
    setE({ ...VISTA_INICIAL, ...resto })
    if (!p) return
    // Las variables se resuelven HOY: la vista de "hoy − 1" abre con la fecha de ayer.
    if (resolverFecha(p.desde) !== resolverFecha(periodo.desde) || resolverFecha(p.hasta) !== resolverFecha(periodo.hasta) || !D) await cargar(p.desde, p.hasta)
    else setPeriodo({ desde: p.desde, hasta: p.hasta })
  }
  async function borrarVista() {
    const v = vistas.find(x => String(x.id) === vistaSel); if (!v) return
    await biApi.cuboBorrarVista(v.id)
    setVistas(vs => vs.filter(x => x.id !== v.id)); setVistaSel(''); avisar('Vista «' + v.nombre + '» borrada')
  }

  // ── Copiar para Excel ──
  function copiar() {
    if (!tabla) return
    const { lineasV, colKeys, celda, calcFila, encab, encabFila, nf, totFila } = tabla
    const head = nf ? E.fila.map(id => cat.porId[id].label) : ['']
    colKeys.forEach(c => { const cl = c === '*' ? 'Total' : etiquetaColumna(cat, E, c); encab.forEach(x => head.push((E.col.length ? cl + ' · ' : '') + x.nombre)) })
    encabFila.forEach(x => head.push(x.nombre))
    const filas = [head]
    const lineas = totFila || !nf ? [...lineasV, { tipo: 'tot', partes: [], L: 0 }] : lineasV
    for (const ln of lineas) {
      const r = ln.tipo === 'fila' ? ln.partes.map((k, q) => etiqueta(cat, E.fila[q], k))
        : [ln.tipo === 'tot' ? 'Total general' : 'Total ' + ln.partes.map((k, q) => etiqueta(cat, E.fila[q], k)).join(' › '), ...Array(Math.max(nf - 1, 0)).fill('')]
      colKeys.forEach((c, ci) => celda(ln.L, ln.partes, c, ci).forEach((x, k) => r.push(numExcel(x, encab[k].tipo))))
      calcFila(ln.L, ln.partes).forEach((x, k) => r.push(numExcel(x, encabFila[k].tipo)))
      filas.push(r)
    }
    const tsv = filas.map(r => r.join('\t')).join('\n')
    navigator.clipboard.writeText(tsv).then(() => avisar('Tabla copiada: pegala en Excel'), () => avisar('No se pudo copiar'))
  }

  // ── Pantalla 1: el período ──
  const atajos = rangoDatos?.hasta ? ATAJOS_PERIODO : []
  if (!D) {
    return (
      <div style={st.pagina}>
        <style>{CSS}</style>
        <Encabezado />
        <section style={st.inicio}>
          <h2 style={{ margin: 0, fontSize: 18 }}>¿Qué período querés analizar?</h2>
          <p style={st.textoSec}>El cubo trabaja solo con las ventas de ese rango. Después armás filas, columnas, valores y cálculos.</p>
          <div style={st.atajos}>
            {atajos.map(([t, d, h]) => <button key={t} type="button" className="cv-atajo" style={st.atajo} onClick={() => setPeriodo({ desde: d, hasta: h })}>{t}</button>)}
          </div>
          <div style={st.fechas}>
            <div style={st.lblFecha}>Desde<FechaPeriodo value={periodo.desde} onChange={v => setPeriodo(p => ({ ...p, desde: v }))} etiqueta="Desde" /></div>
            <div style={st.lblFecha}>Hasta<FechaPeriodo value={periodo.hasta} onChange={v => setPeriodo(p => ({ ...p, hasta: v }))} etiqueta="Hasta" /></div>
            <Boton variante="primary" disabled={cargando} onClick={() => cargar(periodo.desde, periodo.hasta)}>{cargando ? 'Trayendo ventas…' : 'Armar el cubo'}</Boton>
          </div>
          {vistas.length > 0 && (
            <div style={st.fechas}>
              <select style={st.select} value="" onChange={e => elegirVista(e.target.value)} aria-label="Abrir una vista guardada">
                <option value="">…o abrí una vista guardada</option>
                {vistas.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
              </select>
            </div>
          )}
          {error && <p style={st.error}>{error}</p>}
          {rangoDatos?.hasta && <p style={st.nota}>Hay ventas cargadas del {fechaLarga(rangoDatos.desde)} al {fechaLarga(rangoDatos.hasta)}. Hasta {fmtEntero(rangoDatos.max_lineas)} renglones por consulta (más o menos dos años).</p>}
        </section>
        {toast && <div style={st.toast} role="status">{toast}</div>}
      </div>
    )
  }

  // ── Pantalla 2: el cubo ──
  const nombresVal = E.val.map(v => nombreValor(cat, v))
  const dims = cat.campos.filter(c => c.numIdx == null)
  const nums = cat.campos.filter(c => c.numIdx != null)
  const grupos = [...new Set(dims.map(c => c.g))]
  return (
    <div style={st.pagina}>
      <style>{CSS}</style>
      <Encabezado>
        <select style={{ ...st.select, width: 240 }} value={vistaSel} onChange={e => elegirVista(e.target.value)} aria-label="Vistas guardadas">
          <option value="">{vistas.length ? 'Mis vistas…' : 'Todavía no guardaste vistas'}</option>
          {vistas.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
        </select>
        {vistaSel && <Boton variante="ghost" sm onClick={borrarVista}>Borrar vista</Boton>}
        <Boton sm onClick={() => setDlgGuardar(true)}>Guardar vista</Boton>
        <Boton sm onClick={copiar}>Copiar para Excel</Boton>
      </Encabezado>

      <div style={st.barraPeriodo}>
        <strong>Período</strong>
        <FechaPeriodo value={periodo.desde} onChange={v => setPeriodo(p => ({ ...p, desde: v }))} etiqueta="Desde" />
        <span style={st.textoSec}>al</span>
        <FechaPeriodo value={periodo.hasta} onChange={v => setPeriodo(p => ({ ...p, hasta: v }))} etiqueta="Hasta" />
        <Boton variante="primary" sm disabled={cargando} onClick={() => cargar(periodo.desde, periodo.hasta)}>{cargando ? 'Trayendo…' : 'Aplicar'}</Boton>
        <div style={st.atajos}>
          {atajos.map(([t, d, h]) => <button key={t} type="button" className="cv-atajo" style={st.atajo} onClick={() => cargar(d, h)}>{t}</button>)}
        </div>
        <span style={st.textoSec}>{fechaLarga(D.desde)} → {fechaLarga(D.hasta)} · {fmtEntero(N)} renglones</span>
      </div>
      {error && <p style={st.error}>{error}</p>}

      {/* Campos como tags: cada uno se ubica en Filas o Columnas y se filtra desde su menú. */}
      <section style={st.tags} aria-label="Campos">
        {grupos.map(g => (
          <div key={g} style={st.tagGrupo}>
            <span style={st.tagGrupoT}>{g}</span>
            {dims.filter(c => c.g === g).map(c => {
              const z = E.fila.includes(c.id) ? 'fila' : E.col.includes(c.id) ? 'col' : null
              const nf = E.fil[c.id]?.length || 0
              return (
                <button key={c.id} type="button" className="cv-tag" draggable
                  onDragStart={e => { arrastreRef.current = { campo: c.id }; e.dataTransfer.setData('text/plain', c.id) }}
                  onClick={e => menuTag(e, c.id)}
                  style={{ ...st.tag, ...(z === 'fila' ? st.tagFila : z === 'col' ? st.tagCol : null) }}
                  title={c.label + (z ? ' — en ' + ZONAS[z] : '') + (nf ? ' — filtrado' : '')}>
                  {z && <span style={st.tagBadge}>{z === 'fila' ? 'F' : 'C'}{E[z].length > 1 ? E[z].indexOf(c.id) + 1 : ''}</span>}
                  {c.label}
                  {nf > 0 && <span style={st.tagFiltro}>{nf}</span>}
                  <span style={{ color: colors.textMeta }}>▾</span>
                </button>
              )
            })}
          </div>
        ))}
      </section>
      <div style={st.franjas}>
        {Object.entries(ZONAS).map(([z, titulo]) => (
          <Zona key={z} titulo={titulo} horizontal onDrop={e => soltar(e, z)}>
            {E[z].map((id, i) => (
              <Chip key={id} z={z} i={i} filtrado={(E.fil[id] || []).length > 0} arrastreRef={arrastreRef} onMenu={menuChip}
                texto={cat.porId[id].label + ((E.fil[id] || []).length ? ' (' + E.fil[id].length + ')' : '')} />
            ))}
          </Zona>
        ))}
      </div>

      <div style={st.layout}>
        <main style={{ minWidth: 0 }}>
          {tabla ? <>
            <div style={st.resumen}>
              <span>{fmtEntero(tabla.n)} renglones en la vista · {fmtEntero(tabla.filas.length)} filas × {fmtEntero(tabla.visibles.length)} columnas
                {tabla.visibles.length < tabla.cols.length ? ` (${tabla.cols.length - tabla.visibles.length} ocultas, disponibles para cálculos)` : ''}
                {tabla.recorteFilas ? ` · se muestran ${fmtEntero(tabla.lineasV.length)} de ${fmtEntero(tabla.lineas.length)} filas` : ''}
                {tabla.recorteCols ? ` · se muestran ${tabla.colsV.length} de ${tabla.visibles.length} columnas` : ''}</span>
            </div>
            <Tabla t={tabla} E={E} cat={cat} />
          </> : <div style={st.vacio}>Agregá un valor (por ejemplo Facturado) para ver números.</div>}
        </main>

        <aside style={st.panel} aria-label="Valores, totales y cálculos">
          <div style={st.pSec}>
            <h2 style={sx.cardTitle}>Valores</h2>
            <Zona titulo="En la tabla" onDrop={e => soltar(e, 'val')}>
              {E.val.map((v, i) => <Chip key={i} z="val" i={i} arrastreRef={arrastreRef} onMenu={menuChip} oculto={v.oculto}
                texto={nombresVal[i] + (v.como !== 'val' ? ' (' + COMO[v.como] + ')' : '') + (v.oculto ? ' · oculto' : '')} />)}
            </Zona>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {nums.map(c => <button key={c.id} type="button" className="cv-tag" style={st.tag} onClick={() => upd(n => agregarA(n, 'val', c.id))}>+ {c.label}</button>)}
            </div>
          </div>
          <div style={st.pSec}>
            <h2 style={sx.cardTitle}>Totales</h2>
            <label style={st.opc}><input type="checkbox" checked={E.totFila !== false} onChange={e => upd(n => { n.totFila = e.target.checked })} /> Total de filas (renglón Total general)</label>
            <label style={st.opc}><input type="checkbox" checked={E.totCol !== false} onChange={e => upd(n => { n.totCol = e.target.checked })} /> Total de columnas (columna Total)</label>
            <label style={st.opc}><input type="checkbox" checked={E.subtot} onChange={e => upd(n => { n.subtot = e.target.checked })} /> Subtotales por fila</label>
          </div>
          <div style={st.pSec}>
            <h2 style={sx.cardTitle}>Cálculos sobre la vista</h2>
            {E.calcs.length ? E.calcs.map((c, i) => (
              <div key={i} style={st.calcItem}>
                <div style={{ minWidth: 0 }}><strong>{c.nombre}</strong><span style={st.calcF} title={c.formula}>{c.formula}</span></div>
                <button type="button" style={st.x} aria-label="Quitar cálculo" onClick={() => upd(n => n.calcs.splice(i, 1))}>×</button>
              </div>
            )) : <p style={st.nota}>Todavía no hay cálculos.</p>}
            <Boton variante="send" sm onClick={() => (E.val.length ? setDlgCalc(true) : avisar('Primero agregá valores a la tabla'))}>+ Nuevo cálculo</Boton>
            <p style={st.nota}>Entre valores de la celda: <span style={sx.mono}>([Facturado] - [Costo]) / [Facturado]</span>. Entre columnas: <span style={sx.mono}>[Facturado · oct 2026] - [Facturado · sep 2026]</span> (va al final de la fila). Lo que usa un cálculo puede estar oculto.</p>
          </div>
        </aside>
      </div>

      {pop && <div style={{ ...st.pop, left: pop.x, top: pop.y }} onClick={e => e.stopPropagation()}>{pop.contenido}</div>}
      {dlgCalc && <DialogoCalculo nombres={nombresVal} columnas={tabla ? tabla.cols.map(c => etiquetaColumna(cat, E, c)) : []}
        onCerrar={() => setDlgCalc(false)} onAgregar={(c) => { upd(n => n.calcs.push(c)); setDlgCalc(false); avisar('Cálculo «' + c.nombre + '» agregado') }} />}
      {dlgGuardar && <DialogoGuardar inicial={vistas.find(v => String(v.id) === vistaSel)?.nombre || ''} onCerrar={() => setDlgGuardar(false)} onGuardar={guardarVista} />}
      {toast && <div style={st.toast} role="status">{toast}</div>}
    </div>
  )
}

/**
 * Una punta del período: «Fecha fija» (el calendario de siempre) o una fecha
 * VARIABLE — Hoy, primer/último día del mes o del año… ± N días, meses o años.
 * Al lado se ve a qué fecha da hoy.
 */
function FechaPeriodo({ value, onChange, etiqueta }) {
  const variable = esFechaVariable(value)
  const resuelta = resolverFecha(value)
  const cambiarBase = (base) => {
    if (!base) onChange(resuelta)   // vuelve a fija con la fecha que daba
    else onChange({ base, n: variable ? value.n : 0, unidad: variable ? value.unidad || 'd' : 'd' })
  }
  return (
    <span style={st.fechaPeriodo} title={variable ? `${etiquetaFechaVariable(value)} = ${fmtFecha(resuelta)}` : undefined}>
      <select style={{ ...st.select, maxWidth: 210 }} value={variable ? value.base : ''} onChange={e => cambiarBase(e.target.value)} aria-label={`${etiqueta}: tipo de fecha`}>
        <option value="">Fecha fija</option>
        {BASES_FECHA.map(([b, t]) => <option key={b} value={b}>{t}</option>)}
      </select>
      {variable ? (
        <>
          <CantidadFecha n={value.n || 0} onChange={n => onChange({ ...value, n })} etiqueta={etiqueta} />
          <select style={st.select} value={value.unidad || 'd'} onChange={e => onChange({ ...value, unidad: e.target.value })} aria-label={`${etiqueta}: unidad`}>
            {UNIDADES_FECHA.map(([u, plural]) => <option key={u} value={u}>{plural}</option>)}
          </select>
          <span style={{ ...st.textoSec, ...sx.mono }}>= {fmtFecha(resuelta)}</span>
        </>
      ) : (
        <CampoFecha value={value || ''} onChange={onChange} style={st.inputFecha} aria-label={etiqueta} />
      )}
    </span>
  )
}

/**
 * Cuánto se suma o resta a la fecha de referencia. Guarda el TEXTO tal como se
 * escribe: con un campo que convertía cada tecla en número, el "−" solo y el
 * campo vacío volvían a 0 y no se podía escribir "−1" (Martin, 15 oct 2026).
 * El número recién se toma cuando es un entero completo; al salir del campo,
 * lo que quedó a medias vuelve al último valor válido.
 */
function CantidadFecha({ n, onChange, etiqueta }) {
  const [txt, setTxt] = useState(String(n))
  const [nVisto, setNVisto] = useState(n)
  if (n !== nVisto) { setNVisto(n); if (Number(txt) !== n) setTxt(String(n)) }
  return (
    <input type="text" inputMode="numeric" style={{ ...st.input, width: 64, ...sx.mono }} value={txt}
      onChange={e => {
        const t = e.target.value.replace('−', '-').replace(/[^0-9-]/g, '').replace(/(?!^)-/g, '')
        setTxt(t)
        if (/^-?\d+$/.test(t)) onChange(parseInt(t, 10))
      }}
      onBlur={() => { if (!/^-?\d+$/.test(txt)) setTxt(String(n)) }}
      placeholder="0" aria-label={`${etiqueta}: cuánto sumar o restar (con − para restar)`} />
  )
}

function Encabezado({ children }) {
  return (
    <div style={st.top}>
      <div>
        <h1 style={st.h1}>Cubo de ventas</h1>
        <div style={st.textoSec}>Renglones de venta de Presea con todos sus campos. Elegí el período, armá la tabla y calculá sobre la vista.</div>
      </div>
      {children && <div style={st.acciones}>{children}</div>}
    </div>
  )
}

function Zona({ titulo, children, onDrop, horizontal }) {
  const [over, setOver] = useState(false)
  const hijos = (Array.isArray(children) ? children.flat() : [children]).filter(Boolean)
  return (
    <div style={{ ...st.zona, ...(horizontal ? st.zonaH : null), ...(over ? st.zonaOver : null) }}
      onDragOver={e => { e.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { setOver(false); onDrop(e) }}>
      <div style={st.zonaT}>{titulo}</div>
      {hijos.length ? hijos : <div style={st.hint}>{horizontal ? 'Ubicá un campo desde su tag o arrastralo acá' : 'Agregá un valor'}</div>}
    </div>
  )
}

function Chip({ z, i, texto, filtrado, oculto, arrastreRef, onMenu }) {
  return (
    <div data-chip className="cv-chip" draggable title={texto}
      onDragStart={e => { arrastreRef.current = { z, i }; e.dataTransfer.setData('text/plain', String(i)) }}
      onClick={e => onMenu(e, z, i)}
      style={{ ...st.chip, ...(filtrado ? st.chipFiltrado : null), ...(oculto ? { color: colors.textMeta, borderStyle: 'dashed' } : null) }}>
      <span style={st.chipT}>{texto}</span><span style={{ color: colors.textMeta }}>▾</span>
    </div>
  )
}

function MenuValor({ v, res, nombre, nombreBase, onCambio }) {
  const [nom, setNom] = useState(v.nombre || '')
  return <>
    <div style={st.popTit}>{nombre}</div>
    <div style={st.popSep}>Resumir por</div>
    {res.map(r => <button key={r} className="cv-opt" style={v.res === r ? st.optOn : undefined} onClick={() => onCambio({ res: r })}>{RESUMEN[r]}</button>)}
    <div style={st.popSep}>Mostrar como</div>
    {Object.entries(COMO).map(([k, t]) => <button key={k} className="cv-opt" style={v.como === k ? st.optOn : undefined} onClick={() => onCambio({ como: k })}>{t}</button>)}
    <div style={st.popSep}>Nombre</div>
    <div style={{ padding: '2px 6px', display: 'flex', gap: 6 }}>
      <input style={{ ...st.input, flex: 1 }} value={nom} placeholder={nombreBase} onChange={e => setNom(e.target.value)} />
      <Boton sm onClick={() => onCambio({ nombre: nom.trim() })}>OK</Boton>
    </div>
    <button className="cv-opt" onClick={() => onCambio({ oculto: !v.oculto })}>{v.oculto ? 'Mostrar en la tabla' : 'Ocultar en la tabla (sigue en los cálculos)'}</button>
    <button className="cv-opt" onClick={() => onCambio('quitar')}>Quitar de Valores</button>
  </>
}

function MenuFiltro({ id, cat, N, elegidos, onAplicar }) {
  const todos = useMemo(() => valoresDistintos(cat, id, N), [cat, id, N])
  const [sel, setSel] = useState(() => new Set(elegidos))
  const [q, setQ] = useState('')
  const MAXL = 400
  const visibles = todos.filter(k => !q || etiqueta(cat, id, k).toLowerCase().includes(q.toLowerCase()))
  const toggle = k => setSel(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })
  return <>
    <div style={st.popTit}>Filtrar {cat.porId[id].label}</div>
    <div style={{ padding: 4 }}><input style={{ ...st.input, width: '100%' }} type="search" autoFocus placeholder="Buscar" value={q} onChange={e => setQ(e.target.value)} /></div>
    {visibles.slice(0, MAXL).map(k => (
      <label key={k} style={st.ck}><input type="checkbox" checked={sel.has(k)} onChange={() => toggle(k)} /> {etiqueta(cat, id, k)}</label>
    ))}
    {visibles.length > MAXL && <div style={st.popSep}>{fmtEntero(visibles.length - MAXL)} más: buscá para encontrarlos</div>}
    <div style={st.popPie}>
      <Boton variante="ghost" sm onClick={() => setSel(new Set())}>Limpiar</Boton>
      <Boton variante="ghost" sm onClick={() => onAplicar(null)}>Quitar filtro</Boton>
      <Boton variante="primary" sm onClick={() => onAplicar([...sel])}>Aplicar</Boton>
    </div>
  </>
}

function DialogoCalculo({ nombres, columnas, onCerrar, onAgregar }) {
  const [nombre, setNombre] = useState('')
  const [formula, setFormula] = useState('')
  const [fmt, setFmt] = useState('money')
  const [err, setErr] = useState(null)
  const [valCol, setValCol] = useState(nombres[0] || '')
  const [col, setCol] = useState(columnas[0] || '')
  const ref = useRef(null)
  const insertar = n => {
    const el = ref.current, p = el?.selectionStart ?? formula.length, f = el?.selectionEnd ?? p
    setFormula(formula.slice(0, p) + '[' + n + ']' + formula.slice(f)); el?.focus()
  }
  const agregar = () => {
    if (!nombre.trim()) { setErr('Ponele un nombre al cálculo.'); return }
    try { compilar(formula, nombres, new Map(columnas.map(c => [c, c]))) } catch (e) { setErr(e.message); return }
    onAgregar({ nombre: nombre.trim(), formula: formula.trim(), fmt })
  }
  return (
    <Modal title="Nuevo cálculo" onClose={onCerrar} maxWidth="600px">
      <div style={{ display: 'grid', gap: 10 }}>
        <label style={st.lblFecha}>Nombre<input style={st.input} value={nombre} maxLength={40} placeholder="Margen % o Variación oct/sep" onChange={e => setNombre(e.target.value)} autoFocus /></label>
        <label style={st.lblFecha}>Fórmula<input ref={ref} style={{ ...st.input, ...sx.mono }} value={formula} placeholder="([Facturado] - [Costo]) / [Facturado]" onChange={e => setFormula(e.target.value)} /></label>
        <div style={st.lblFecha}>Valores de la celda (tocá para insertar)
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {nombres.map(n => <button key={n} type="button" style={st.tok} onClick={() => insertar(n)}>[{n}]</button>)}
          </div>
        </div>
        {columnas.length > 0 && (
          <div style={st.lblFecha}>Un valor de una columna puntual (para comparar columnas, por ejemplo un mes contra otro)
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <select style={st.select} value={valCol} onChange={e => setValCol(e.target.value)} aria-label="Valor">{nombres.map(n => <option key={n}>{n}</option>)}</select>
              <span style={st.textoSec}>de la columna</span>
              <select style={st.select} value={col} onChange={e => setCol(e.target.value)} aria-label="Columna">{columnas.map(c => <option key={c}>{c}</option>)}</select>
              <Boton sm onClick={() => insertar(valCol + ' · ' + col)}>Insertar</Boton>
            </div>
          </div>
        )}
        <p style={st.nota}>Operadores + - * / y paréntesis. Con valores de la celda se calcula en cada celda (también en subtotales y totales). Si usa una columna puntual, sale una vez por fila, al final de la tabla. Las columnas filtradas y los valores ocultos siguen disponibles.</p>
        <label style={st.lblFecha}>Formato
          <select style={st.select} value={fmt} onChange={e => setFmt(e.target.value)}>
            <option value="money">Importe</option><option value="num">Número</option><option value="pct">Porcentaje</option>
          </select>
        </label>
        {err && <p style={st.error}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <Boton variante="ghost" onClick={onCerrar}>Cancelar</Boton>
          <Boton variante="primary" onClick={agregar}>Agregar a la tabla</Boton>
        </div>
      </div>
    </Modal>
  )
}

function DialogoGuardar({ inicial, onCerrar, onGuardar }) {
  const [nombre, setNombre] = useState(inicial)
  return (
    <Modal title="Guardar vista" onClose={onCerrar} maxWidth="460px">
      <div style={{ display: 'grid', gap: 10 }}>
        <label style={st.lblFecha}>Nombre de la vista<input style={st.input} value={nombre} maxLength={80} placeholder="Bodegas por mes" autoFocus onChange={e => setNombre(e.target.value)} /></label>
        <p style={st.nota}>Guarda el período, las filas, columnas, valores, filtros y cálculos. Las fechas variables (por ejemplo «Hoy − 1 día» o «Primer día del año») se recalculan cada vez que abrís la vista. Si ya tenés una vista con ese nombre, se reemplaza.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <Boton variante="ghost" onClick={onCerrar}>Cancelar</Boton>
          <Boton variante="primary" disabled={!nombre.trim()} onClick={() => onGuardar(nombre.trim())}>Guardar</Boton>
        </div>
      </div>
    </Modal>
  )
}

function Tabla({ t, E, cat }) {
  const { lineasV, colsV, colKeys, celda, calcFila, encab, encabFila, nf, totFila } = t
  const nVal = encab.length
  const nCF = encabFila.length
  const hayTotCol = E.col.length > 0 && colKeys.includes('*')
  const top = E.col.length * 25
  const encabezados = []
  if (E.col.length) {
    for (let lvl = 0; lvl < E.col.length; lvl++) {
      const celdas = []
      if (lvl === 0) celdas.push(<th key="rh" className="rh" rowSpan={E.col.length} colSpan={Math.max(nf, 1)} style={{ top: 0 }}>{E.col.map(id => cat.porId[id].label).join(' › ')}</th>)
      let q = 0
      while (nVal && q < colsV.length) {
        const pref = colsV[q].split(SEP).slice(0, lvl + 1).join(SEP)
        let span = 1
        while (q + span < colsV.length && colsV[q + span].split(SEP).slice(0, lvl + 1).join(SEP) === pref) span++
        celdas.push(<th key={q} className="ch" colSpan={span * nVal} style={{ top: lvl * 25 }}>{etiqueta(cat, E.col[lvl], colsV[q].split(SEP)[lvl])}</th>)
        q += span
      }
      if (lvl === 0 && hayTotCol) celdas.push(<th key="tot" className="ch tc" rowSpan={E.col.length} colSpan={nVal} style={{ top: 0 }}>Total</th>)
      if (lvl === 0 && nCF) celdas.push(<th key="cf" className="ch vh c" rowSpan={E.col.length} colSpan={nCF} style={{ top: 0 }}>Cálculos entre columnas</th>)
      encabezados.push(<tr key={'h' + lvl}>{celdas}</tr>)
    }
  }
  encabezados.push(
    <tr key="hv">
      {(nf ? E.fila : ['']).map((id, k) => <th key={'f' + k} className="rh" style={{ top, ...(k ? { left: 'auto' } : null) }}>{id ? cat.porId[id].label : ''}</th>)}
      {colKeys.flatMap(c => encab.map((x, k) => <th key={c + k} className={'vh' + (x.calc ? ' c' : '') + (c === '*' ? ' tc' : '')} style={{ top }}>{x.nombre}</th>))}
      {encabFila.map((x, k) => <th key={'cf' + k} className="vh c" style={{ top }}>{x.nombre}</th>)}
    </tr>,
  )
  const numeros = (L, partes) => [
    ...colKeys.flatMap((c, ci) => celda(L, partes, c, ci).map((x, k) => (
      <td key={c + k} className={(c === '*' ? 'tc ' : '') + (encab[k].calc ? 'cv ' : '') + (x != null && x < 0 ? 'neg' : '')}>{fmtCelda(x, encab[k].tipo)}</td>
    ))),
    ...calcFila(L, partes).map((x, k) => (
      <td key={'cf' + k} className={'cv ' + (x != null && x < 0 ? 'neg' : '')}>{fmtCelda(x, encabFila[k].tipo)}</td>
    )),
  ]
  // La fila anterior (para no repetir el rótulo del grupo padre); un subtotal corta la racha.
  const anterior = lineasV.map((ln, r) => (r > 0 && lineasV[r - 1].tipo === 'fila' ? lineasV[r - 1].partes : []))
  const filas = lineasV.map((ln, r) => {
    if (ln.tipo === 'sub') {
      return <tr key={r} className="sub"><td className="rl" colSpan={nf}>Total {ln.partes.map((k, q) => etiqueta(cat, E.fila[q], k)).join(' › ')}</td>{numeros(ln.L, ln.partes)}</tr>
    }
    const prev = anterior[r]
    const celdasFila = nf ? ln.partes.map((k, q) => {
      const rep = q < nf - 1 && prev.slice(0, q + 1).join(SEP) === ln.partes.slice(0, q + 1).join(SEP)
      const tx = etiqueta(cat, E.fila[q], k)
      return <td key={'r' + q} className={'rl' + (rep ? ' vacia' : '')} title={tx} style={q ? { left: 'auto' } : undefined}>{tx}</td>
    }) : [<td key="r0" className="rl">Total</td>]
    return <tr key={r} className="fl">{celdasFila}{numeros(nf, ln.partes)}</tr>
  })
  return (
    <div style={st.tablaWrap}>
      <table className="cv-tabla">
        <thead>{encabezados}</thead>
        <tbody>
          {filas}
          {nf > 0 && totFila && <tr className="tot"><td className="rl" colSpan={nf}>Total general</td>{numeros(0, [])}</tr>}
        </tbody>
      </table>
    </div>
  )
}

const fechaLarga = iso => (iso ? iso.slice(8, 10) + '/' + iso.slice(5, 7) + '/' + iso.slice(0, 4) : '')

const st = {
  pagina: { padding: '16px 20px 32px', fontFamily: font.sans, color: colors.text },
  top: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10, paddingBottom: 10, borderBottom: `1px solid ${colors.border}` },
  h1: { margin: 0, fontSize: 20, fontWeight: 650 },
  acciones: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0, maxWidth: '100%' },
  textoSec: { color: colors.textSec, fontSize: text.sec, margin: 0 },
  nota: { color: colors.textMeta, fontSize: text.meta, margin: 0 },
  error: { color: colors.err, background: colors.errBg, borderRadius: radius.sm, padding: '6px 10px', fontSize: text.sec, margin: '8px 0 0' },
  inicio: { maxWidth: 560, margin: '48px auto', border: `1px solid #cbd5e1`, borderRadius: radius.md, padding: 22, display: 'grid', gap: 14, background: colors.bg },
  atajos: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  atajo: { border: `1px solid #cbd5e1`, background: colors.bg, borderRadius: radius.pill, padding: '3px 10px', fontSize: text.sec, cursor: 'pointer', color: colors.text },
  fechas: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' },
  lblFecha: { display: 'grid', gap: 4, fontSize: text.sec, color: colors.textSec },
  inputFecha: { width: 120 },
  fechaPeriodo: { display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  input: { border: `1px solid #cbd5e1`, borderRadius: radius.sm, padding: '5px 8px', fontSize: 13, fontFamily: font.sans, minWidth: 0, color: colors.text, background: colors.bg },
  select: { border: `1px solid #cbd5e1`, borderRadius: radius.sm, padding: '5px 8px', fontSize: 13, background: colors.bg, maxWidth: '100%', color: colors.text },
  barraPeriodo: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', border: `1px solid #cbd5e1`, borderRadius: radius.sm, background: colors.bgSoft },
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16, marginTop: 12, alignItems: 'start' },
  resumen: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 6, color: colors.textSec, fontSize: text.sec, marginBottom: 6 },
  tablaWrap: { overflow: 'auto', maxHeight: 'calc(100vh - 230px)', border: `1px solid #cbd5e1`, borderRadius: radius.xs },
  vacio: { padding: '40px 16px', textAlign: 'center', color: colors.textSec, border: `1px dashed #cbd5e1`, borderRadius: radius.sm },
  panel: { border: `1px solid #cbd5e1`, borderRadius: radius.sm, background: colors.bgSoft, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'sticky', top: 12 },
  pSec: { padding: 10, borderBottom: `1px solid ${colors.border}`, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8 },
  campos: { maxHeight: 280, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.xs, background: colors.bg, padding: 2 },
  cgrupo: { fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: colors.textMeta, padding: '7px 8px 3px' },
  zonas: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  zona: { minHeight: 76, border: `1px solid #cbd5e1`, borderRadius: radius.xs, background: colors.bg, padding: 4, display: 'flex', flexDirection: 'column', gap: 3 },
  zonaOver: { background: colors.linkBg, borderColor: colors.link },
  zonaH: { minHeight: 0, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', padding: '4px 6px', gap: 4 },
  franjas: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 10, padding: '8px 10px', border: `1px solid #cbd5e1`, borderRadius: radius.sm, background: colors.bg },
  tagGrupo: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
  tagGrupoT: { fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: colors.textMeta, marginRight: 2 },
  tag: { display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${colors.border}`, background: colors.bgSoft, borderRadius: radius.pill, padding: '2px 9px', fontSize: text.sec, cursor: 'pointer', color: colors.text, fontFamily: font.sans },
  tagFila: { background: colors.linkBg, borderColor: colors.linkBorder, color: colors.link, fontWeight: 600 },
  tagCol: { background: '#ecfdf5', borderColor: '#6ee7b7', color: '#047857', fontWeight: 600 },
  tagBadge: { fontSize: 10, fontWeight: 700, border: '1px solid currentColor', borderRadius: 3, padding: '0 3px', lineHeight: '14px' },
  tagFiltro: { fontSize: 10.5, fontWeight: 700, background: colors.text, color: '#fff', borderRadius: radius.pill, padding: '0 6px', lineHeight: '16px' },
  segmento: { display: 'flex', gap: 0, margin: '2px 6px 4px', border: `1px solid #cbd5e1`, borderRadius: radius.sm, overflow: 'hidden' },
  segBtn: { flex: 1, border: 0, borderRight: `1px solid #cbd5e1`, background: colors.bg, padding: '5px 6px', fontSize: 12.5, cursor: 'pointer', color: colors.text },
  segOn: { background: colors.link, color: '#fff', fontWeight: 600 },
  opc: { display: 'flex', gap: 6, alignItems: 'center', fontSize: text.sec, color: colors.text },
  zonaT: { fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: colors.textSec, padding: '2px 4px' },
  hint: { color: colors.textMeta, fontSize: text.meta, padding: '2px 4px' },
  chip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, border: `1px solid ${colors.border}`, background: colors.bgSoft, borderRadius: 3, padding: '3px 6px', fontSize: text.sec, cursor: 'grab', minWidth: 0 },
  chipFiltrado: { borderColor: colors.linkBorder, background: colors.linkBg },
  chipT: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  calcItem: { display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center', border: '1px solid #ddd6fe', background: '#f5f3ff', color: '#6d28d9', borderRadius: radius.xs, padding: '4px 4px 4px 8px' },
  calcF: { fontFamily: font.mono, fontSize: 11, color: colors.textSec, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  x: { border: 0, background: 'none', color: colors.textMeta, cursor: 'pointer', fontSize: 15 },
  pop: { position: 'fixed', zIndex: 900, background: colors.bg, border: `1px solid #cbd5e1`, borderRadius: radius.sm, boxShadow: '0 12px 32px rgba(15, 23, 42, .2)', padding: 6, width: 290, maxHeight: 420, overflow: 'auto' },
  popTit: { fontWeight: 650, padding: '4px 6px 6px', fontSize: 13 },
  popSep: { fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: colors.textMeta, padding: '8px 8px 2px' },
  popPie: { position: 'sticky', bottom: -6, background: colors.bg, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', gap: 6, padding: '6px 4px 2px' },
  optOn: { color: colors.link, fontWeight: 600 },
  ck: { display: 'flex', gap: 8, padding: '3px 6px', alignItems: 'center', fontSize: 13 },
  tok: { border: `1px solid #cbd5e1`, background: colors.bgSoft, borderRadius: radius.xs, padding: '2px 7px', fontFamily: font.mono, fontSize: 11.5, cursor: 'pointer', color: colors.text },
  toast: { position: 'fixed', left: '50%', bottom: 18, transform: 'translateX(-50%)', background: colors.text, color: '#fff', padding: '8px 14px', borderRadius: radius.sm, fontSize: text.sec, zIndex: 1000 },
}
